import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import Navbar from './components/Navbar';
import PublicNavbar from './components/PublicNavbar';

// Lazy load components
const LandingPage = React.lazy(() => import('./components/LandingPage'));
const About = React.lazy(() => import('./components/About'));
const Contact = React.lazy(() => import('./components/Contact'));
const Privacy = React.lazy(() => import('./components/Privacy'));
const Terms = React.lazy(() => import('./components/Terms'));
const CoinList = React.lazy(() => import('./components/CoinList'));
const CoinDetail = React.lazy(() => import('./components/CoinDetail'));
const Collections = React.lazy(() => import('./components/Collections'));
const CollectionDetail = React.lazy(() => import('./components/CollectionDetail'));
const PublicCollection = React.lazy(() => import('./components/PublicCollection'));
const UserProfile = React.lazy(() => import('./components/UserProfile'));
const AddCoin = React.lazy(() => import('./components/AddCoin'));
const AddCollection = React.lazy(() => import('./components/AddCollection'));
const Profile = React.lazy(() => import('./components/Profile'));
const Auth = React.lazy(() => import('./components/Auth'));
const ResetPassword = React.lazy(() => import('./components/ResetPassword'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

function App() {
  const [session, setSession] = useState(supabase.auth.getSession());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('App: Initializing auth state');
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('App: Auth session loaded:', session ? 'authenticated' : 'unauthenticated');
      setSession(session);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('App: Auth state changed:', _event);
      setSession(session);
    });

    return () => {
      console.log('App: Cleaning up auth subscription');
      subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return <LoadingFallback />;
  }

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-gray-50">
        {session ? <Navbar session={session} /> : <PublicNavbar />}
        <main className="container mx-auto px-4 py-8">
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Public routes that should be accessible whether logged in or not */}
              <Route path="/" element={session ? <CoinList /> : <LandingPage />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              
              {/* Public profile and collection routes */}
              <Route path="/@:username" element={<UserProfile />} />
              <Route path="/@:username/:slug" element={<PublicCollection />} />
              
              {/* Authentication routes */}
              <Route 
                path="/login" 
                element={
                  session ? (
                    <Navigate to="/" replace />
                  ) : (
                    <Auth view="sign_in" onAuthComplete={() => window.location.reload()} />
                  )
                } 
              />
              <Route 
                path="/register" 
                element={
                  session ? (
                    <Navigate to="/" replace />
                  ) : (
                    <Auth view="sign_up" onAuthComplete={() => window.location.reload()} />
                  )
                } 
              />
              <Route 
                path="/reset-password" 
                element={
                  session ? (
                    <Navigate to="/" replace />
                  ) : (
                    <Auth view="reset_password" onAuthComplete={() => window.location.reload()} />
                  )
                } 
              />
              
              {/* Protected routes */}
              {session ? (
                <>
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/coin/:id" element={<CoinDetail />} />
                  <Route path="/collections" element={<Collections />} />
                  <Route path="/collection/:id" element={<CollectionDetail />} />
                  <Route path="/add-coin" element={<AddCoin />} />
                  <Route path="/add-collection" element={<AddCollection />} />
                </>
              ) : (
                <Route path="*" element={<Navigate to="/" replace />} />
              )}
            </Routes>
          </Suspense>
        </main>
      </div>
    </BrowserRouter>
  );
}

export default App;